import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { logMyEvent } from '../lib/audit';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: string;
  targetHint?: 'sidebar' | 'header' | 'main' | 'fab' | 'center';
  route?: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Bem-vindo ao PandaPro!',
    description: 'Vamos fazer um tour rápido pelos recursos principais da plataforma. Você pode navegar pelas etapas ou fechar a qualquer momento e retomar depois.',
    icon: 'ri-hand-heart-line',
    targetHint: 'center',
    route: '/dashboard',
  },
  {
    id: 'dashboard',
    title: 'Dashboard — Visão Geral',
    description: 'Aqui você tem um resumo completo: saldo total, receitas, despesas, alertas de vencimentos e insights automáticos. É o ponto de partida perfeito para acompanhar suas finanças.',
    icon: 'ri-dashboard-line',
    targetHint: 'main',
    route: '/dashboard',
  },
  {
    id: 'contas',
    title: 'Contas Bancárias',
    description: 'Cadastre e gerencie todas as suas contas: corrente, poupança, investimento e carteira digital. Você pode ver o extrato de cada conta separadamente.',
    icon: 'ri-bank-line',
    targetHint: 'sidebar',
    route: '/contas',
  },
  {
    id: 'lancamentos',
    title: 'Lançamentos',
    description: 'Registre todas as suas receitas e despesas. Categorize, filtre por período, defina recorrências e controle tudo em um só lugar.',
    icon: 'ri-exchange-dollar-line',
    targetHint: 'sidebar',
    route: '/lancamentos',
  },
  {
    id: 'cartoes',
    title: 'Cartões de Crédito',
    description: 'Controle seus cartões de crédito, acompanhe o limite disponível, faturas abertas e realize pagamentos diretamente pela plataforma.',
    icon: 'ri-bank-card-line',
    targetHint: 'sidebar',
    route: '/cartoes',
  },
  {
    id: 'metas',
    title: 'Metas Financeiras',
    description: 'Defina objetivos como a compra de um carro, viagem ou reserva de emergência. Acompanhe o progresso e registre contribuições para manter o foco.',
    icon: 'ri-focus-3-line',
    targetHint: 'sidebar',
    route: '/metas',
  },
  {
    id: 'relatorios',
    title: 'Relatórios & Insights',
    description: 'Analise seus gastos por categoria, compare períodos, projete o saldo futuro e exporte relatórios completos em PDF. Disponível no plano Premium.',
    icon: 'ri-bar-chart-2-line',
    targetHint: 'main',
    route: '/relatorios',
  },
  {
    id: 'done',
    title: 'Você está pronto!',
    description: 'Esse foi o tour básico do PandaPro. Qualquer dúvida, acesse o suporte pelo botão de ajuda ou reinicie este tutorial em Perfil → Ajuda & Tutorial a qualquer momento.',
    icon: 'ri-check-double-line',
    targetHint: 'center',
    route: '/dashboard',
  },
];

// ─── localStorage helpers ─────────────────────────────────────────────────────
const SEEN_KEY = 'panda_tour_seen';

export const hasTourBeenSeen = (userId: string): boolean => {
  if (!userId) return false;
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as Record<string, boolean>;
    return !!data[userId];
  } catch {
    return false;
  }
};

export const markTourSeen = (userId: string): void => {
  if (!userId) return;
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const data = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    data[userId] = true;
    localStorage.setItem(SEEN_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
};

// ─── Context ──────────────────────────────────────────────────────────────────
interface OnboardingTourContextValue {
  tourActive: boolean;
  currentStep: number;
  totalSteps: number;
  step: TourStep;
  startTour: (manual?: boolean) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  restartTour: () => void;
}

const OnboardingTourContext = createContext<OnboardingTourContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
interface OnboardingTourProviderProps {
  children: ReactNode;
  userId?: string;
}

export const OnboardingTourProvider = ({ children, userId = '' }: OnboardingTourProviderProps) => {
  const [tourActive, setTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  // Keep userId in a ref so callbacks always read the latest value
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const totalSteps = TOUR_STEPS.length;

  const navigateToStep = useCallback((stepIdx: number) => {
    const route = TOUR_STEPS[stepIdx]?.route;
    if (route) navigate(route);
  }, [navigate]);

  const startTour = useCallback((manual = false) => {
    setCurrentStep(0);
    setTourActive(true);
    navigateToStep(0);
    logMyEvent('update', {
      type: manual ? 'onboarding_started_manual' : 'onboarding_started_auto',
    });
  }, [navigateToStep]);

  const restartTour = useCallback(() => {
    startTour(true);
  }, [startTour]);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => {
      const next = prev + 1;
      if (next >= totalSteps) {
        // Last step → complete tour
        setTourActive(false);
        markTourSeen(userIdRef.current);   // ← userId correto via ref
        logMyEvent('update', { type: 'onboarding_completed' });
        navigate('/dashboard');
        return 0;
      }
      navigateToStep(next);
      return next;
    });
  }, [totalSteps, navigateToStep, navigate]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => {
      const p = Math.max(0, prev - 1);
      navigateToStep(p);
      return p;
    });
  }, [navigateToStep]);

  const skipTour = useCallback(() => {
    setTourActive(false);
    markTourSeen(userIdRef.current);       // ← userId correto via ref
    logMyEvent('update', { type: 'onboarding_skipped' });
    navigate('/dashboard');
    setCurrentStep(0);
  }, [navigate]);

  const step = TOUR_STEPS[currentStep] ?? TOUR_STEPS[0];

  return (
    <OnboardingTourContext.Provider
      value={{
        tourActive,
        currentStep,
        totalSteps,
        step,
        startTour,
        nextStep,
        prevStep,
        skipTour,
        restartTour,
      }}
    >
      {children}
    </OnboardingTourContext.Provider>
  );
};

export const useOnboardingTour = (): OnboardingTourContextValue => {
  const ctx = useContext(OnboardingTourContext);
  if (!ctx) throw new Error('useOnboardingTour must be used inside OnboardingTourProvider');
  return ctx;
};
